'use client'

import { useState, useEffect, useCallback } from 'react'
import { useCurrency } from '@/lib/currency'
import { getApiUrl } from '@/lib/apiUrl'

interface Application {
  id: string
  property: {
    id: string
    title: string
    location: string
    price: number
    rentalType?: 'short-term' | 'long-term'
  }
  client: {
    id: string
    firstName?: string
    lastName?: string
    email: string
  }
  landlord: {
    id: string
    firstName?: string
    lastName?: string
    email: string
  }
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn'
  createdAt: string
  reviewedAt?: string
  payment?: {
    id: string
    amount: number
    currency: string
    status: string
    isEscrow: boolean
    escrowStatus?: 'held' | 'released' | 'refunded'
    escrowHeldAt?: string
    escrowExpiresAt?: string
    escrowReleasedAt?: string
    propertyVisited: boolean
    documentsReceived: boolean
  }
}

interface RentalApplicationManagementProps {
  onUpdate?: () => void
}

export default function RentalApplicationManagement({ onUpdate }: RentalApplicationManagementProps) {
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [releasingEscrow, setReleasingEscrow] = useState<string | null>(null)
  const { format } = useCurrency()

  const loadApplications = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) return

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/applications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        console.error('Error loading applications:', response.statusText)
        return
      }

      const data = await response.json()
      const applicationsArray = Array.isArray(data) ? data : (data.applications || [])

      // Load payment info for each application
      const applicationsWithPayments = await Promise.all(
        applicationsArray.map(async (app: any) => {
          try {
            const paymentResponse = await fetch(`${apiUrl}/payments/history?applicationId=${app._id || app.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            })

            let payment = null
            if (paymentResponse.ok) {
              const paymentData = await paymentResponse.json()
              const payments = paymentData.payments || []
              payment = payments.find((p: any) => p.status === 'completed') || null
            }

            return {
              ...app,
              id: app._id || app.id,
              property: app.property ? {
                ...app.property,
                id: app.property._id || app.property.id
              } : app.property,
              client: app.client ? {
                ...app.client,
                id: app.client._id || app.client.id
              } : app.client,
              landlord: app.landlord ? {
                ...app.landlord,
                id: app.landlord._id || app.landlord.id
              } : app.landlord,
              payment: payment ? {
                id: payment._id || payment.id,
                amount: payment.amount,
                currency: payment.currency,
                status: payment.status,
                isEscrow: payment.isEscrow,
                escrowStatus: payment.escrowStatus,
                escrowHeldAt: payment.escrowHeldAt,
                escrowExpiresAt: payment.escrowExpiresAt,
                escrowReleasedAt: payment.escrowReleasedAt,
                propertyVisited: payment.propertyVisited,
                documentsReceived: payment.documentsReceived
              } : undefined
            }
          } catch (error) {
            console.error('Error loading payment for application:', error)
            return {
              ...app,
              id: app._id || app.id
            }
          }
        })
      )

      setApplications(applicationsWithPayments)
    } catch (error) {
      console.error('Error loading applications:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadApplications()
  }, [loadApplications])

  const handleReleaseEscrow = async (paymentId: string) => {
    setReleasingEscrow(paymentId)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        alert('Please log in')
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/payments/${paymentId}/escrow/release`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        alert(errorData.message || 'Failed to release escrow')
        return
      }

      alert('Escrow released successfully')
      loadApplications()
      onUpdate?.()
    } catch (error) {
      console.error('Error releasing escrow:', error)
      alert('Failed to release escrow')
    } finally {
      setReleasingEscrow(null)
    }
  }

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true
    return app.status === filter
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getEscrowStatusColor = (status?: string) => {
    switch (status) {
      case 'held':
        return 'bg-blue-100 text-blue-800'
      case 'released':
        return 'bg-green-100 text-green-800'
      case 'refunded':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return 'Invalid Date'
    }
  }

  const getDaysUntilExpiry = (expiresAt?: string) => {
    if (!expiresAt) return null
    try {
      const expiry = new Date(expiresAt)
      const now = new Date()
      const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      return diff
    } catch {
      return null
    }
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading applications...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Rental Applications</h2>
        <button
          onClick={loadApplications}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            All ({applications.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-md ${filter === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Pending ({applications.filter(a => a.status === 'pending').length})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-md ${filter === 'approved' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Approved ({applications.filter(a => a.status === 'approved').length})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-md ${filter === 'rejected' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Rejected ({applications.filter(a => a.status === 'rejected').length})
          </button>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Landlord</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Escrow</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredApplications.map((app) => (
              <tr key={app.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{app.property?.title}</div>
                  <div className="text-sm text-gray-500">{app.property?.location}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {app.client?.firstName && app.client?.lastName
                      ? `${app.client.firstName} ${app.client.lastName}`
                      : app.client?.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {app.landlord?.firstName && app.landlord?.lastName
                      ? `${app.landlord.firstName} ${app.landlord.lastName}`
                      : app.landlord?.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(app.status)}`}>
                    {app.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {app.payment ? (
                    <div className="text-sm">
                      <div className="text-gray-900">{format(app.payment.amount)}</div>
                      <div className="text-gray-500">{app.payment.status}</div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No payment</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {app.payment?.isEscrow ? (
                    <div className="space-y-1">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEscrowStatusColor(app.payment.escrowStatus)}`}>
                        {app.payment.escrowStatus || 'held'}
                      </span>
                      {app.payment.escrowStatus === 'held' && app.payment.escrowExpiresAt && (
                        <div className="text-xs text-gray-500">
                          {(() => {
                            const days = getDaysUntilExpiry(app.payment.escrowExpiresAt)
                            return days !== null ? `${days} days left` : 'Expired'
                          })()}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        Visited: {app.payment.propertyVisited ? '✓' : '✗'}
                      </div>
                      <div className="text-xs text-gray-500">
                        Docs: {app.payment.documentsReceived ? '✓' : '✗'}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedApplication(app)
                        setShowDetails(true)
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                    {app.payment?.isEscrow && app.payment.escrowStatus === 'held' && (
                      <button
                        onClick={() => handleReleaseEscrow(app.payment!.id)}
                        disabled={releasingEscrow === app.payment!.id}
                        className="text-green-600 hover:text-green-900 disabled:text-gray-400"
                      >
                        {releasingEscrow === app.payment!.id ? 'Releasing...' : 'Release Escrow'}
                      </button>
                    )}
                    {/* Admin can reject at any time */}
                    {app.status !== 'rejected' && (
                      <button
                        onClick={async () => {
                          if (!confirm('Are you sure you want to reject this application?')) return
                          try {
                            const token = localStorage.getItem('authToken')
                            if (!token) {
                              alert('Please log in')
                              return
                            }
                            const apiUrl = getApiUrl()
                            const response = await fetch(`${apiUrl}/applications/${app.id}`, {
                              method: 'PUT',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({ status: 'rejected' })
                            })
                            if (!response.ok) {
                              const errorData = await response.json().catch(() => ({}))
                              alert(errorData.message || 'Failed to reject application')
                              return
                            }
                            alert('Application rejected successfully')
                            loadApplications()
                            onUpdate?.()
                          } catch (error) {
                            console.error('Error rejecting application:', error)
                            alert('Failed to reject application')
                          }
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Reject
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      {showDetails && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
              <button
                onClick={() => {
                  setShowDetails(false)
                  setSelectedApplication(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">Property</h4>
                <p className="text-gray-600">{selectedApplication.property?.title}</p>
                <p className="text-sm text-gray-500">{selectedApplication.property?.location}</p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900">Client</h4>
                <p className="text-gray-600">
                  {selectedApplication.client?.firstName && selectedApplication.client?.lastName
                    ? `${selectedApplication.client.firstName} ${selectedApplication.client.lastName}`
                    : selectedApplication.client?.email}
                </p>
                <p className="text-sm text-gray-500">{selectedApplication.client?.email}</p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900">Landlord</h4>
                <p className="text-gray-600">
                  {selectedApplication.landlord?.firstName && selectedApplication.landlord?.lastName
                    ? `${selectedApplication.landlord.firstName} ${selectedApplication.landlord.lastName}`
                    : selectedApplication.landlord?.email}
                </p>
                <p className="text-sm text-gray-500">{selectedApplication.landlord?.email}</p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900">Status</h4>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedApplication.status)}`}>
                  {selectedApplication.status}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  Applied: {formatDate(selectedApplication.createdAt)}
                </p>
                {selectedApplication.reviewedAt && (
                  <p className="text-sm text-gray-500">
                    Reviewed: {formatDate(selectedApplication.reviewedAt)}
                  </p>
                )}
              </div>

              {selectedApplication.payment && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Payment Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className="text-gray-900 font-medium">{format(selectedApplication.payment.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="text-gray-900">{selectedApplication.payment.status}</span>
                    </div>
                    {selectedApplication.payment.isEscrow && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Escrow Status:</span>
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEscrowStatusColor(selectedApplication.payment.escrowStatus)}`}>
                            {selectedApplication.payment.escrowStatus || 'held'}
                          </span>
                        </div>
                        {selectedApplication.payment.escrowHeldAt && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Held Since:</span>
                            <span className="text-gray-900">{formatDate(selectedApplication.payment.escrowHeldAt)}</span>
                          </div>
                        )}
                        {selectedApplication.payment.escrowExpiresAt && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Expires:</span>
                            <span className="text-gray-900">
                              {formatDate(selectedApplication.payment.escrowExpiresAt)}
                              {(() => {
                                const days = getDaysUntilExpiry(selectedApplication.payment.escrowExpiresAt)
                                return days !== null ? ` (${days} days left)` : ' (Expired)'
                              })()}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Property Visited:</span>
                          <span className="text-gray-900">{selectedApplication.payment.propertyVisited ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Documents Received:</span>
                          <span className="text-gray-900">{selectedApplication.payment.documentsReceived ? 'Yes' : 'No'}</span>
                        </div>
                        {selectedApplication.payment.escrowStatus === 'held' && (
                          <button
                            onClick={() => handleReleaseEscrow(selectedApplication.payment!.id)}
                            disabled={releasingEscrow === selectedApplication.payment!.id}
                            className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                          >
                            {releasingEscrow === selectedApplication.payment!.id ? 'Releasing...' : 'Release Escrow to Landlord'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Admin Actions */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Admin Actions</h4>
                <div className="flex gap-2">
                  {selectedApplication.status !== 'rejected' && (
                    <button
                      onClick={async () => {
                        if (!confirm('Are you sure you want to reject this application? This action cannot be undone.')) return
                        try {
                          const token = localStorage.getItem('authToken')
                          if (!token) {
                            alert('Please log in')
                            return
                          }
                          const apiUrl = getApiUrl()
                          const response = await fetch(`${apiUrl}/applications/${selectedApplication.id}`, {
                            method: 'PUT',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ status: 'rejected' })
                          })
                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}))
                            alert(errorData.message || 'Failed to reject application')
                            return
                          }
                          alert('Application rejected successfully')
                          setShowDetails(false)
                          setSelectedApplication(null)
                          loadApplications()
                          onUpdate?.()
                        } catch (error) {
                          console.error('Error rejecting application:', error)
                          alert('Failed to reject application')
                        }
                      }}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Reject Application
                    </button>
                  )}
                  {selectedApplication.status === 'pending' && (
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('authToken')
                          if (!token) {
                            alert('Please log in')
                            return
                          }
                          const apiUrl = getApiUrl()
                          const response = await fetch(`${apiUrl}/applications/${selectedApplication.id}`, {
                            method: 'PUT',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ status: 'approved' })
                          })
                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}))
                            alert(errorData.message || 'Failed to approve application')
                            return
                          }
                          alert('Application approved successfully')
                          setShowDetails(false)
                          setSelectedApplication(null)
                          loadApplications()
                          onUpdate?.()
                        } catch (error) {
                          console.error('Error approving application:', error)
                          alert('Failed to approve application')
                        }
                      }}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Approve Application
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

